/**
 * Noktalar (endpoint) — LED ekran, VR istasyonu, projeksiyon...
 *
 * ── AJAN TOKEN'I BIR KEZ GOSTERILIR ──────────────────────────────────────────
 * Sunucu yalnizca ozetini saklar; geri getirilemez. Bu yuzden olusturma
 * sonrasi modal ZORLA acilir ve kullanici kopyalayana kadar kapanmaz mesaji
 * verir. "Sonra bakarim" diyebilecegi bir yerde gostermek, kayip token demek.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiRequestError } from '@/lib/api';
import { useMe } from '@/lib/auth';
import { useToast } from '@/lib/toast';
import { copy, useI18n } from '@/lib/i18n';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  Input,
  LoadingBlock,
  Modal,
  PageHeader,
  Select,
  StatusDot,
  TableWrap,
} from '@/components/ui';
import { ENDPOINT_KIND_LABEL, formatRelative } from '@/lib/format';
import type { CatalogItem, Endpoint, EndpointCommand, EndpointCommandAction, EndpointKind } from '@shared/index';
import './Endpoints.css';

const EMPTY_FORM = {
  name: '',
  kind: 'led-screen' as EndpointKind,
  location: '',
  resolution: '',
  aspectRatio: '',
  geometry: '',
  hasAudio: false,
  screenPreviewAllowed: false,
  useHours: true,
  start: '07:00',
  end: '00:00',
};

type EndpointTelemetryEvent = {
  id: string;
  level: 'debug' | 'info' | 'warn' | 'error';
  kind: string;
  source: string;
  productSlug: string | null;
  message: string | null;
  metadata: Record<string, string | number | boolean | null>;
  occurredAt: string;
  receivedAt: string;
};

export default function Endpoints() {
  const { language } = useI18n();
  const c = (turkish: string, english: string) => copy(language, turkish, english);
  const me = useMe();
  const toast = useToast();
  const qc = useQueryClient();

  const [editing, setEditing] = useState<Endpoint | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [newToken, setNewToken] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<{ kind: 'rotate' | 'archive'; endpoint: Endpoint } | null>(null);
  const [softwareEndpoint, setSoftwareEndpoint] = useState<Endpoint | null>(null);
  const [search, setSearch] = useState('');
  const [kindFilter, setKindFilter] = useState<'all' | EndpointKind>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | Endpoint['status']>('all');
  const [systemVolume, setSystemVolume] = useState(50);
  const [contentLocked, setContentLocked] = useState(false);
  const [lockedProductSlug, setLockedProductSlug] = useState('');
  const [confirmRestart, setConfirmRestart] = useState(false);
  const [previewSession, setPreviewSession] = useState<{ id: string; expiresAt: string } | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const canManage = me.user.role === 'owner' || me.user.role === 'customer_admin';

  const endpoints = useQuery({
    queryKey: ['endpoints'],
    queryFn: () => api.get<{ items: Endpoint[] }>('/endpoints'),
    // Sahadaki cihaz durumu degisir; 30 saniyede bir tazele.
    refetchInterval: 30_000,
  });

  const desktopProducts = useQuery({
    queryKey: ['catalog', { ownedOnly: true, delivery: 'desktop-install' }],
    queryFn: () => api.get<{ items: CatalogItem[] }>('/catalog', { ownedOnly: true, pageSize: 100 }),
    select: (data) => ({ items: data.items.filter((product) => product.delivery === 'desktop-install') }),
  });

  const commands = useQuery({
    queryKey: ['endpoint-commands', softwareEndpoint?.id],
    queryFn: () => api.get<{ items: EndpointCommand[] }>(`/endpoints/${softwareEndpoint!.id}/commands`),
    enabled: softwareEndpoint !== null,
    refetchInterval: softwareEndpoint ? 10_000 : false,
  });

  const telemetry = useQuery({
    queryKey: ['endpoint-telemetry', softwareEndpoint?.id],
    queryFn: () => api.get<{ items: EndpointTelemetryEvent[] }>(`/endpoints/${softwareEndpoint!.id}/telemetry`, { limit: 60 }),
    enabled: softwareEndpoint !== null,
    refetchInterval: softwareEndpoint ? 10_000 : false,
  });

  function buildBody() {
    return {
      name: form.name.trim(),
      kind: form.kind,
      location: form.location.trim(),
      capabilities: {
        ...(form.resolution ? { resolution: form.resolution.trim() } : {}),
        ...(form.aspectRatio ? { aspectRatio: form.aspectRatio.trim() } : {}),
        ...(form.geometry ? { geometry: form.geometry.trim() } : {}),
        hasAudio: form.hasAudio,
        screenPreviewAllowed: form.screenPreviewAllowed,
      },
      operatingHours: form.useHours
        ? { tz: 'Europe/Istanbul', start: form.start, end: form.end }
        : null,
    };
  }

  const create = useMutation({
    mutationFn: () => api.post<{ id: string; agentToken: string }>('/endpoints', buildBody()),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['endpoints'] });
      setCreating(false);
      setForm(EMPTY_FORM);
      setFields({});
      setNewToken(data.agentToken);
    },
    onError: (err) => handleError(err),
  });

  const update = useMutation({
    mutationFn: (id: string) => api.patch(`/endpoints/${id}`, buildBody()),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['endpoints'] });
      setEditing(null);
      setFields({});
      toast.success(c('Cihaz güncellendi.', 'Device updated.'));
    },
    onError: (err) => handleError(err),
  });

  const archive = useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
      api.patch(`/endpoints/${id}`, { archived }),
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: ['endpoints'] });
      setPendingAction(null);
      toast.success(vars.archived ? c('Cihaz arşivlendi.', 'Device archived.') : c('Cihaz geri alındı.', 'Device restored.'));
    },
    onError: (err) => handleError(err),
  });

  const rotate = useMutation({
    mutationFn: (id: string) => api.post<{ agentToken: string }>(`/endpoints/${id}/rotate-token`),
    onSuccess: (data) => {
      setPendingAction(null);
      setNewToken(data.agentToken);
    },
    onError: (err) => handleError(err),
  });

  const sendCommand = useMutation({
    mutationFn: ({ endpointId, productId, action }: { endpointId: string; productId: string; action: EndpointCommandAction }) =>
      api.post(`/endpoints/${endpointId}/commands`, { productId, action }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['endpoint-commands', softwareEndpoint?.id] });
      void qc.invalidateQueries({ queryKey: ['endpoints'] });
      toast.success(c('İşlem CORTEX kuyruğuna alındı.', 'Action queued for CORTEX.'));
    },
    onError: (err) => handleError(err),
  });

  const sendSystemCommand = useMutation({
    mutationFn: (payload: { action: 'restart'; delaySeconds: number } | { action: 'set_volume'; percent: number } | { action: 'set_content_lock'; locked: boolean; productSlug: string | null }) =>
      api.post(`/endpoints/${softwareEndpoint!.id}/system-commands`, payload),
    onSuccess: (_data, payload) => {
      void qc.invalidateQueries({ queryKey: ['endpoint-commands', softwareEndpoint?.id] });
      setConfirmRestart(false);
      toast.success(payload.action === 'restart'
        ? c('Yeniden başlatma CORTEX kuyruğuna alındı.', 'Restart queued for CORTEX.')
        : c('Cihaz ayarı CORTEX kuyruğuna alındı.', 'Device setting queued for CORTEX.'));
    },
    onError: (err) => handleError(err),
  });

  const startPreview = useMutation({
    mutationFn: () => api.post<{ id: string; expiresAt: string }>(`/endpoints/${softwareEndpoint!.id}/preview-sessions`, {}),
    onSuccess: (value) => setPreviewSession(value),
    onError: (err) => handleError(err),
  });
  const stopPreview = useMutation({
    mutationFn: () => api.delete(`/endpoints/${softwareEndpoint!.id}/preview-sessions/${previewSession!.id}`),
    onSettled: () => { setPreviewSession(null); setPreviewUrl((url) => { if (url) URL.revokeObjectURL(url); return null; }); },
  });
  useEffect(() => {
    if (!previewSession || !softwareEndpoint) return;
    let disposed = false;
    const load = async () => {
      try {
        const { blob } = await api.download(`/endpoints/${softwareEndpoint.id}/preview-sessions/${previewSession.id}/frame`);
        if (disposed || blob.size === 0) return;
        const next = URL.createObjectURL(blob);
        setPreviewUrl((current) => { if (current) URL.revokeObjectURL(current); return next; });
      } catch { /* İlk kare hazırlanırken ya da oturum kapanırken sessizce bekle. */ }
    };
    void load();
    const timer = window.setInterval(() => void load(), 2_000);
    return () => { disposed = true; window.clearInterval(timer); };
  }, [previewSession, softwareEndpoint]);

  function handleError(err: unknown) {
    if (err instanceof ApiRequestError) {
      setFields(err.fields ?? {});
      toast.error(err.message);
    } else {
      toast.error(c('Sunucuya ulaşılamadı.', 'Could not reach the server.'));
    }
  }

  function closeSoftwareModal() {
    if (softwareEndpoint && previewSession) {
      void api.delete(`/endpoints/${softwareEndpoint.id}/preview-sessions/${previewSession.id}`).catch(() => undefined);
    }
    setPreviewSession(null);
    setPreviewUrl((url) => { if (url) URL.revokeObjectURL(url); return null; });
    setSoftwareEndpoint(null);
  }

  function openEdit(endpoint: Endpoint) {
    const caps = endpoint.capabilities ?? {};
    setForm({
      name: endpoint.name,
      kind: endpoint.kind,
      location: endpoint.location,
      resolution: caps.resolution ?? '',
      aspectRatio: caps.aspectRatio ?? '',
      geometry: caps.geometry ?? '',
      hasAudio: caps.hasAudio === true,
      screenPreviewAllowed: caps.screenPreviewAllowed === true,
      useHours: endpoint.operatingHours !== null,
      start: endpoint.operatingHours?.start ?? '07:00',
      end: endpoint.operatingHours?.end ?? '00:00',
    });
    setFields({});
    setEditing(endpoint);
  }

  const items = endpoints.data?.items ?? [];
  const selectedSoftwareEndpoint = softwareEndpoint
    ? items.find((endpoint) => endpoint.id === softwareEndpoint.id) ?? softwareEndpoint
    : null;
  const visibleItems = items.filter((endpoint) => {
    const locale = language === 'tr' ? 'tr-TR' : 'en-US';
    const needle = search.trim().toLocaleLowerCase(locale);
    const matchesSearch = !needle
      || `${endpoint.name} ${endpoint.location} ${ENDPOINT_KIND_LABEL[endpoint.kind] ?? endpoint.kind}`
        .toLocaleLowerCase(locale)
        .includes(needle);
    return matchesSearch
      && (kindFilter === 'all' || endpoint.kind === kindFilter)
      && (statusFilter === 'all' || endpoint.status === statusFilter);
  });

  return (
    <div className="endpoint-page stack stack--lg">
      <PageHeader
        title={c('Cihazlar', 'Devices')}
        description={c('Ürünlerin çalıştığı ekranları, VR istasyonlarını, bilgisayarları ve deneyim noktalarını yönetin.', 'Manage the displays, VR stations, computers, and experience points where your products run.')}
        actions={
          canManage ? (
            <Button
              variant="primary"
              onClick={() => {
                setForm(EMPTY_FORM);
                setFields({});
                setCreating(true);
              }}
            >
              {c('Cihaz ekle', 'Add device')}
            </Button>
          ) : undefined
        }
      />

      <div className="ep-toolbar" aria-label={c('Cihaz filtreleri', 'Device filters')}>
        <Input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={c('Cihaz veya konum ara', 'Search device or location')}
          aria-label={c('Cihaz veya konum ara', 'Search device or location')}
        />
        <Select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as typeof statusFilter)} aria-label={c('Bağlantı durumu', 'Connection status')}>
          <option value="all">{c('Tüm durumlar', 'All statuses')}</option>
          <option value="online">{c('Çevrimiçi', 'Online')}</option>
          <option value="playing">{c('Oynatıyor', 'Playing')}</option>
          <option value="offline">{c('Çevrimdışı', 'Offline')}</option>
        </Select>
        <Select value={kindFilter} onChange={(event) => setKindFilter(event.target.value as typeof kindFilter)} aria-label={c('Cihaz türü', 'Device type')}>
          <option value="all">{c('Tüm cihaz türleri', 'All device types')}</option>
          {Object.entries(ENDPOINT_KIND_LABEL).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </Select>
      </div>

      {endpoints.isLoading ? (
        <LoadingBlock rows={4} label={c('Cihazlar yükleniyor', 'Loading devices')} />
      ) : endpoints.isError ? (
        <ErrorState message={c('Cihazlar alınamadı.', 'Could not load devices.')} onRetry={() => void endpoints.refetch()} />
      ) : items.length === 0 ? (
        <Card>
          <EmptyState
            title={c('Henüz cihaz yok', 'No devices yet')}
            description={c('Bir LED ekran, VR istasyonu veya kurulum ekleyerek başlayın. Eklediğiniz her cihaz için sahadaki sisteme tanıtacağınız bir bağlantı anahtarı üretilir.', 'Start by adding an LED display, VR station, or installation. Hub generates a connection key for every device you add.')}
            action={
              canManage ? (
                <Button variant="primary" onClick={() => setCreating(true)}>
                  {c('İlk cihazı ekle', 'Add your first device')}
                </Button>
              ) : (
                <span className="small muted">{c('Cihaz eklemek için yönetici yetkisi gerekir.', 'Administrator permission is required to add a device.')}</span>
              )
            }
          />
        </Card>
      ) : visibleItems.length === 0 ? (
        <Card><EmptyState title={c('Eşleşen cihaz yok', 'No matching devices')} description={c('Arama veya filtre seçimini değiştirerek yeniden deneyin.', 'Change your search or filters and try again.')} /></Card>
      ) : (
        <Card padded={false} className="ep-tablecard">
          <TableWrap caption={c('Cihazlar ve deneyim noktaları', 'Devices and experience points')}>
            <thead><tr><th>{c('Cihaz', 'Device')}</th><th>{c('Mekân', 'Venue')}</th><th>{c('Tür', 'Type')}</th><th>{c('Çalışma saatleri', 'Operating hours')}</th><th>{c('Bağlantı', 'Connection')}</th><th>{c('Son sinyal', 'Last signal')}</th><th aria-label={c('İşlemler', 'Actions')} /></tr></thead>
            <tbody>
              {visibleItems.map((endpoint) => (
                <tr key={endpoint.id}>
                  <td data-label={c('Cihaz', 'Device')}><div className="ep-device"><span className="ep-device__icon" aria-hidden="true"><DeviceGlyph /></span><div><strong>{endpoint.name}</strong>{endpoint.nowPlaying?.productName ? <small>{endpoint.nowPlaying.productName}</small> : null}</div></div></td>
                  <td data-label={c('Mekân', 'Venue')}>{endpoint.location || c('Konum tanımlanmadı', 'Location not defined')}</td>
                  <td data-label={c('Tür', 'Type')}><Badge>{ENDPOINT_KIND_LABEL[endpoint.kind] ?? endpoint.kind}</Badge></td>
                  <td data-label={c('Çalışma', 'Hours')}>{endpoint.operatingHours ? `${endpoint.operatingHours.start}–${endpoint.operatingHours.end}` : c('Sürekli', 'Always on')}</td>
                  <td data-label={c('Bağlantı', 'Connection')}><StatusDot tone={endpoint.status === 'playing' ? 'primary' : endpoint.status === 'online' ? 'success' : 'danger'} label={endpoint.status === 'playing' ? c('Oynatıyor', 'Playing') : endpoint.status === 'online' ? c('Çevrimiçi', 'Online') : c('Çevrimdışı', 'Offline')} /></td>
                  <td data-label={c('Son sinyal', 'Last signal')} className="small muted">{formatRelative(endpoint.lastSeenAt)}</td>
                  <td data-label={c('İşlemler', 'Actions')}>
                    {canManage ? <div className="ep-actions">
                      <Button size="sm" variant="primary" onClick={() => {
                        setSoftwareEndpoint(endpoint);
                        setContentLocked(endpoint.capabilities.contentLock?.locked === true);
                        setLockedProductSlug(endpoint.capabilities.contentLock?.productSlug ?? '');
                      }}>{c('Yönet', 'Manage')}</Button>
                      <Button size="sm" onClick={() => openEdit(endpoint)}>{c('Düzenle', 'Edit')}</Button>
                      <Button size="sm" variant="ghost" onClick={() => setPendingAction({ kind: 'rotate', endpoint })}>{c('Anahtar', 'Key')}</Button>
                      <Button size="sm" variant="ghost" onClick={() => setPendingAction({ kind: 'archive', endpoint })}>{c('Arşivle', 'Archive')}</Button>
                    </div> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        </Card>
      )}

      {/* ── Ekleme / duzenleme ─────────────────────────────────────────────── */}
      <Modal
        open={creating || editing !== null}
        onClose={() => {
          setCreating(false);
          setEditing(null);
        }}
        title={editing ? c('Cihazı düzenle', 'Edit device') : c('Cihaz ekle', 'Add device')}
        description={editing
          ? c('Cihaz kimliğini, kurulum bağlamını ve ürün uyumluluğunu güncelleyin.', 'Update the device identity, installation context, and product compatibility.')
          : c('Yeni cihazı veya deneyim noktasını operasyon alanınıza tanımlayın.', 'Add a new device or experience point to your operation.')}
        size="lg"
        footer={
          <>
            <Button
              onClick={() => {
                setCreating(false);
                setEditing(null);
              }}
            >
              {c('Vazgeç', 'Cancel')}
            </Button>
            <Button
              variant="primary"
              loading={create.isPending || update.isPending}
              onClick={() => (editing ? update.mutate(editing.id) : create.mutate())}
            >
              {editing ? c('Kaydet', 'Save') : c('Ekle', 'Add')}
            </Button>
          </>
        }
      >
        <div className="stack">
          <Field label={c('Ad', 'Name')} required error={fields['name']}>
            {({ id, invalid }) => (
              <Input
                id={id}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                invalid={invalid}
                placeholder={c('Meydan Ekranı', 'Town Square Display')}
              />
            )}
          </Field>

          <Field label={c('Tür', 'Type')} required>
            {({ id }) => (
              <Select
                id={id}
                value={form.kind}
                onChange={(e) => setForm({ ...form, kind: e.target.value as EndpointKind })}
              >
                {Object.entries(ENDPOINT_KIND_LABEL).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field label={c('Konum', 'Location')} error={fields['location']}>
            {({ id, invalid }) => (
              <Input
                id={id}
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                invalid={invalid}
                placeholder={c('Konya, Kültürpark', 'London, Exhibition Hall')}
              />
            )}
          </Field>

          <Field
            label={c('Çözünürlük', 'Resolution')}
            hint={c('Ürün uyumluluğu bu bilgiye göre kontrol edilir. Boş bırakırsanız oran isteyen ürünler planlanamaz.', 'Product compatibility is checked against this value. Products that require an aspect ratio cannot be scheduled if it is empty.')}
            error={fields['capabilities.resolution']}
          >
            {({ id, invalid }) => (
              <Input
                id={id}
                value={form.resolution}
                onChange={(e) => setForm({ ...form, resolution: e.target.value })}
                invalid={invalid}
                placeholder="3840x1280"
              />
            )}
          </Field>

          <Field
            label={c('Ekran oranı', 'Aspect ratio')}
            hint={c('Kavisli veya L köşe ekranlarda gerçek oran çözünürlükten farklı olabilir. Boşsa çözünürlükten hesaplanır.', 'On curved or L-shaped displays, the actual ratio may differ from the resolution. If empty, it is calculated from the resolution.')}
            error={fields['capabilities.aspectRatio']}
          >
            {({ id, invalid }) => (
              <Input
                id={id}
                value={form.aspectRatio}
                onChange={(e) => setForm({ ...form, aspectRatio: e.target.value })}
                invalid={invalid}
                placeholder="3:1"
              />
            )}
          </Field>

          <Field label={c('Geometri', 'Geometry')} hint={c('Düz dikdörtgen değilse: L-köşe, kavisli…', 'If it is not a flat rectangle: L-shaped, curved…')}>
            {({ id }) => (
              <Input
                id={id}
                value={form.geometry}
                onChange={(e) => setForm({ ...form, geometry: e.target.value })}
              />
            )}
          </Field>

          <Checkbox
            label={c('Ses çıkışı var', 'Audio output available')}
            checked={form.hasAudio}
            onChange={(e) => setForm({ ...form, hasAudio: e.target.checked })}
          />

          <Checkbox
            label={c('Güvenli canlı ekran önizlemesine izin ver', 'Allow secure live screen preview')}
            checked={form.screenPreviewAllowed}
            onChange={(e) => setForm({ ...form, screenPreviewAllowed: e.target.checked })}
          />

          <Checkbox
            label={c('Çalışma saatleri tanımla', 'Set operating hours')}
            checked={form.useHours}
            onChange={(e) => setForm({ ...form, useHours: e.target.checked })}
          />

          {form.useHours && (
            <div className="row" style={{ gap: 'var(--space-3)' }}>
              <Field label={c('Açılış', 'Opens')}>
                {({ id }) => (
                  <Input
                    id={id}
                    type="time"
                    value={form.start}
                    onChange={(e) => setForm({ ...form, start: e.target.value })}
                  />
                )}
              </Field>
              <Field label={c('Kapanış', 'Closes')} hint={c('00:00 = gece yarısı', '00:00 = midnight')}>
                {({ id }) => (
                  <Input
                    id={id}
                    type="time"
                    value={form.end}
                    onChange={(e) => setForm({ ...form, end: e.target.value })}
                  />
                )}
              </Field>
            </div>
          )}
        </div>
      </Modal>

      <Modal
        open={softwareEndpoint !== null}
        onClose={closeSoftwareModal}
        title={c('CORTEX ürünleri', 'CORTEX products')}
        description={selectedSoftwareEndpoint
          ? c(`“${selectedSoftwareEndpoint.name}” cihazındaki kurulumları uzaktan yönetin. Cihaz çevrimdışıysa işlem bağlandığında başlar.`, `Manage installations on “${selectedSoftwareEndpoint.name}” remotely. If the device is offline, the action starts when it reconnects.`)
          : c('Cihaz ürünlerini uzaktan yönetin.', 'Manage device products remotely.')}
        size="lg"
        footer={<Button onClick={closeSoftwareModal}>{c('Kapat', 'Close')}</Button>}
      >
        {selectedSoftwareEndpoint ? (
          <div className="ep-software stack">
            <div className="ep-software__summary">
              <StatusDot
                tone={selectedSoftwareEndpoint.status === 'offline' ? 'danger' : 'success'}
                label={selectedSoftwareEndpoint.status === 'offline' ? c('Çevrimdışı · işlemler bekler', 'Offline · actions will wait') : c('CORTEX bağlı', 'CORTEX connected')}
              />
              <span className="small muted">{Object.keys(selectedSoftwareEndpoint.installedVersions).length} {c('kurulu ürün', 'installed products')}</span>
            </div>
            <section className="ep-system-controls" aria-labelledby="ep-system-controls-title">
              <header>
                <div><h3 id="ep-system-controls-title">{c('Cihaz denetimleri', 'Device controls')}</h3><p>{c('Ses, çalışma kilidi ve yeniden başlatma komutları cihaz çevrimiçiyken güvenle uygulanır.', 'Volume, content lock and restart commands are applied securely while the device is online.')}</p></div>
                <Button size="sm" variant="ghost" onClick={() => setConfirmRestart(true)}>{c('Yeniden başlat', 'Restart')}</Button>
              </header>
              <div className="ep-system-controls__grid">
                <div className="ep-volume-control">
                  <label htmlFor="endpoint-volume">{c('Sistem sesi', 'System volume')} <output htmlFor="endpoint-volume">{systemVolume}</output></label>
                  <input id="endpoint-volume" type="range" min="0" max="100" value={systemVolume} onChange={(event) => setSystemVolume(Number(event.target.value))} />
                  <Button size="sm" loading={sendSystemCommand.isPending} onClick={() => sendSystemCommand.mutate({ action: 'set_volume', percent: systemVolume })}>{c('Sesi uygula', 'Apply volume')}</Button>
                </div>
                <div className="ep-lock-control">
                  <Checkbox checked={contentLocked} onChange={(event) => setContentLocked(event.target.checked)} label={c('İçerik başlatmayı kilitle', 'Lock content launch')} />
                  {contentLocked ? <Select aria-label={c('İzin verilen içerik', 'Allowed content')} value={lockedProductSlug} onChange={(event) => setLockedProductSlug(event.target.value)}><option value="">{c('Hiçbir içerik başlatılamaz', 'Block all content')}</option>{(desktopProducts.data?.items ?? []).map((product) => <option key={product.id} value={product.slug}>{product.name}</option>)}</Select> : null}
                  <Button size="sm" loading={sendSystemCommand.isPending} onClick={() => sendSystemCommand.mutate({ action: 'set_content_lock', locked: contentLocked, productSlug: contentLocked && lockedProductSlug ? lockedProductSlug : null })}>{c('Kilidi uygula', 'Apply lock')}</Button>
                </div>
              </div>
              <div className="ep-preview-control">
                <div><strong>{c('Canlı ekran önizlemesi', 'Live screen preview')}</strong><small>{selectedSoftwareEndpoint.capabilities.screenPreviewAllowed ? c('İzin açık; görüntü kalıcı olarak saklanmaz.', 'Permission is on; frames are not stored permanently.') : c('Önce cihaz düzenlemeden müşteri iznini açın.', 'Enable customer permission in device settings first.')}</small></div>
                {previewSession ? <Button size="sm" loading={stopPreview.isPending} onClick={() => stopPreview.mutate()}>{c('Önizlemeyi kapat', 'Stop preview')}</Button> : <Button size="sm" variant="primary" disabled={selectedSoftwareEndpoint.status === 'offline' || selectedSoftwareEndpoint.capabilities.screenPreviewAllowed !== true || selectedSoftwareEndpoint.capabilities.screenPreviewSupported !== true} loading={startPreview.isPending} onClick={() => startPreview.mutate()}>{c('Önizlemeyi başlat', 'Start preview')}</Button>}
              </div>
              {previewSession && <div className="ep-preview-frame">{previewUrl ? <img src={previewUrl} alt={c(`${selectedSoftwareEndpoint.name} canlı ekranı`, `${selectedSoftwareEndpoint.name} live screen`)} /> : <LoadingBlock rows={2} label={c('Cihazdan ilk kare bekleniyor', 'Waiting for the first frame')} />}</div>}
            </section>
            {desktopProducts.isLoading ? <LoadingBlock rows={3} label={c('Ürünler yükleniyor', 'Loading products')} /> : null}
            {desktopProducts.isError ? <ErrorState message={c('Ürünler alınamadı.', 'Could not load products.')} onRetry={() => void desktopProducts.refetch()} /> : null}
            <div className="ep-software__grid">
              {(desktopProducts.data?.items ?? []).map((product) => {
                const installedVersion = selectedSoftwareEndpoint.installedVersions[product.slug] ?? null;
                const active = commands.data?.items.find((command) => command.productSlug === product.slug && (command.status === 'queued' || command.status === 'running'));
                const hasUpdate = Boolean(installedVersion && product.latestVersion && installedVersion !== product.latestVersion);
                const action: EndpointCommandAction = installedVersion ? (hasUpdate ? 'update' : 'uninstall') : 'install';
                const label = active
                  ? active.status === 'running' ? c('Cihaz işliyor', 'Device is processing') : c('Sırada', 'Queued')
                  : action === 'install' ? c('Cihaza yükle', 'Install on device')
                    : action === 'update' ? c('Güncelle', 'Update') : c('Kaldır', 'Uninstall');
                return (
                  <article key={product.id} className="ep-product">
                    <div className="ep-product__media" style={product.coverUrl ? { backgroundImage: `url(${product.coverUrl})` } : undefined} aria-hidden="true" />
                    <div className="ep-product__body">
                      <div><span className="eyebrow">{product.category}</span><strong>{product.name}</strong></div>
                      <p className="small muted">{installedVersion ? `${c('Kurulu', 'Installed')} ${installedVersion}` : c('Kurulu değil', 'Not installed')}{hasUpdate ? ` · ${c('Yeni', 'New')} ${product.latestVersion}` : ''}</p>
                      <Button
                        size="sm"
                        variant={action === 'uninstall' ? 'ghost' : 'primary'}
                        disabled={Boolean(active) || sendCommand.isPending}
                        onClick={() => sendCommand.mutate({ endpointId: selectedSoftwareEndpoint.id, productId: product.id, action })}
                      >{label}</Button>
                    </div>
                  </article>
                );
              })}
            </div>
            {(commands.data?.items.length ?? 0) > 0 ? (
              <section className="ep-command-history">
                <h3>{c('Son işlemler', 'Recent actions')}</h3>
                {commands.data!.items.slice(0, 6).map((command) => (
                  <div key={command.id}>
                    <span><strong>{command.productName}</strong><small>{commandLabel(command.action, language)}{command.semanticReceipt ? ` · ${command.semanticReceipt.status === 'accepted' ? c('Ürün doğruladı', 'Verified by product') : c('Ürün reddetti', 'Rejected by product')}` : ''}</small></span>
                    <Badge tone={command.status === 'succeeded' ? 'success' : command.status === 'failed' ? 'danger' : 'primary'}>{commandStatus(command.status, language)}</Badge>
                  </div>
                ))}
              </section>
            ) : null}
            <section className="ep-telemetry" aria-labelledby="ep-telemetry-title">
              <header>
                <div>
                  <h3 id="ep-telemetry-title">{c('Cihaz zaman çizelgesi', 'Device timeline')}</h3>
                  <p>{c('CORTEX bağlantısı, indirme, oyun ve komut olayları. Kimlik bilgileri kaydedilmeden önce gizlenir.', 'CORTEX connection, download, game and command events. Credentials are redacted before storage.')}</p>
                </div>
                {telemetry.isFetching ? <span className="small muted">{c('Güncelleniyor…', 'Refreshing…')}</span> : null}
              </header>
              {telemetry.isLoading ? (
                <LoadingBlock rows={3} label={c('Cihaz olayları yükleniyor', 'Loading device events')} />
              ) : telemetry.isError ? (
                <ErrorState message={c('Zaman çizelgesi alınamadı.', 'Timeline unavailable.')} onRetry={() => void telemetry.refetch()} />
              ) : (telemetry.data?.items.length ?? 0) === 0 ? (
                <div className="ep-telemetry__empty">
                  <strong>{c('Henüz cihaz olayı yok', 'No device events yet')}</strong>
                  <span>{c('Güncel CORTEX bağlandığında olaylar burada görünür.', 'Events appear here when the current CORTEX connects.')}</span>
                </div>
              ) : (
                <ol className="ep-telemetry__list">
                  {telemetry.data!.items.slice(0, 12).map((event) => (
                    <li key={event.id} className={`ep-telemetry__event ep-telemetry__event--${event.level}`}>
                      <span className="ep-telemetry__dot" aria-hidden="true" />
                      <div>
                        <strong>{telemetryLabel(event.kind, language)}</strong>
                        <small>{event.message || [event.productSlug, event.source].filter(Boolean).join(' · ')}</small>
                      </div>
                      <time dateTime={event.occurredAt}>{formatRelative(event.occurredAt)}</time>
                    </li>
                  ))}
                </ol>
              )}
            </section>
          </div>
        ) : null}
      </Modal>

      <ConfirmDialog
        open={confirmRestart}
        onClose={() => setConfirmRestart(false)}
        onConfirm={() => sendSystemCommand.mutate({ action: 'restart', delaySeconds: 60 })}
        loading={sendSystemCommand.isPending}
        title={c('Cihazı yeniden başlat', 'Restart device')}
        description={c('Çalışan içerikler duracak ve cihaz kısa bir hazırlık süresinin ardından yeniden başlayacak.', 'Running content will stop and the device will restart after a short preparation period.')}
        confirmLabel={c('Yeniden başlat', 'Restart')}
      />

      {/* ── Token gosterimi ────────────────────────────────────────────────── */}
      <Modal
        open={newToken !== null}
        onClose={() => setNewToken(null)}
        title={c('Bağlantı anahtarı', 'Connection key')}
        description={c('Bu anahtar yalnızca bir kez gösterilir. Sahadaki cihazı Wondeon Hub hesabınıza bağlamak için kullanın.', 'This key is shown only once. Use it to connect the on-site device to your Wondeon Hub account.')}
        footer={
          <Button variant="primary" onClick={() => setNewToken(null)}>
            {c('Kopyaladım, kapat', 'I copied it, close')}
          </Button>
        }
      >
        <div className="stack">
          <p className="small">
            {c('Bu anahtarı sahadaki cihaza tanıtın.', 'Enter this key on the on-site device.')} <strong>{c('Bir daha gösterilmeyecek', 'It will not be shown again')}</strong> — {c('sunucu yalnızca özetini saklıyor. Kaybederseniz yeni bir anahtar üretmeniz gerekir.', 'the server stores only its digest. If you lose it, you must generate a new key.')}
          </p>
          <div className="ep-token">
            <code>{newToken}</code>
            <Button
              size="sm"
              onClick={() => {
                void navigator.clipboard
                  .writeText(newToken ?? '')
                  .then(() => toast.success(c('Anahtar panoya kopyalandı.', 'Key copied to the clipboard.')))
                  .catch(() => toast.error(c('Kopyalanamadı. Metni elle seçip kopyalayın.', 'Could not copy. Select and copy the text manually.')));
              }}
            >
              {c('Kopyala', 'Copy')}
            </Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={pendingAction !== null}
        onClose={() => setPendingAction(null)}
        title={pendingAction?.kind === 'rotate' ? c('Bağlantı anahtarını yenile', 'Rotate connection key') : c('Cihazı arşivle', 'Archive device')}
        description={pendingAction?.kind === 'rotate'
          ? c(`“${pendingAction.endpoint.name}” için yeni anahtar üretilecek. Sahadaki cihaz yeni anahtar tanıtılana kadar bağlanamayacak.`, `A new key will be generated for “${pendingAction.endpoint.name}”. The on-site device cannot connect until the new key is entered.`)
          : c(`“${pendingAction?.endpoint.name ?? ''}” cihazı aktif listeden kaldırılacak. Planlanmış oturumlar etkilenmeyecek.`, `“${pendingAction?.endpoint.name ?? ''}” will be removed from the active list. Scheduled sessions will not be affected.`)}
        confirmLabel={pendingAction?.kind === 'rotate' ? c('Anahtarı yenile', 'Rotate key') : c('Arşivle', 'Archive')}
        danger={pendingAction?.kind === 'archive'}
        loading={rotate.isPending || archive.isPending}
        onConfirm={() => {
          if (!pendingAction) return;
          if (pendingAction.kind === 'rotate') rotate.mutate(pendingAction.endpoint.id);
          else archive.mutate({ id: pendingAction.endpoint.id, archived: true });
        }}
      />
    </div>
  );
}

function telemetryLabel(kind: string, language: 'tr' | 'en'): string {
  const labels: Record<string, [string, string]> = {
    APP_STARTED: ['CORTEX başlatıldı', 'CORTEX started'],
    HEARTBEAT: ['Cihaz bağlantısı doğrulandı', 'Device connection verified'],
    NETWORK_LOCKED: ['Bağlantı kesildi', 'Connection lost'],
    NETWORK_RESTORED: ['Bağlantı geri geldi', 'Connection restored'],
    DOWNLOAD_STARTED: ['İndirme başladı', 'Download started'],
    DOWNLOAD_SUCCEEDED: ['İndirme tamamlandı', 'Download completed'],
    DOWNLOAD_FAILED: ['İndirme başarısız', 'Download failed'],
    PACKAGE_VERIFIED: ['Paket doğrulandı', 'Package verified'],
    GAME_PROCESS_STARTED: ['Oyun süreci başlatıldı', 'Game process started'],
    GAME_READY_ACCEPTED: ['Oyun hazır kabul edildi', 'Game ready accepted'],
    GAME_READY_REJECTED: ['Oyun hazır bilgisi reddedildi', 'Game ready rejected'],
    ROUND_RESULT_ACCEPTED: ['Tur sonucu doğrulandı', 'Round result verified'],
    GAME_EXITED: ['Oyun kapandı', 'Game exited'],
    GAME_CRASHED: ['Oyun beklenmedik şekilde kapandı', 'Game crashed'],
    COMMAND_RECEIVED: ['Uzaktan komut alındı', 'Remote command received'],
    COMMAND_SUCCEEDED: ['Uzaktan komut tamamlandı', 'Remote command completed'],
    COMMAND_FAILED: ['Uzaktan komut başarısız', 'Remote command failed'],
    DISPLAY_PLAN_APPLIED: ['Etkinlik planı uygulandı', 'Event plan applied'],
    DISPLAY_PLAN_REJECTED: ['Etkinlik planı reddedildi', 'Event plan rejected'],
    LOG_ERROR: ['CORTEX hatası', 'CORTEX error'],
  };
  const label = labels[kind];
  return label ? label[language === 'tr' ? 0 : 1] : kind.replaceAll('_', ' ').toLocaleLowerCase(language === 'tr' ? 'tr-TR' : 'en-US');
}

function DeviceGlyph() {
  return <svg viewBox="0 0 24 24" width="20" height="20" fill="none"><rect x="3" y="4" width="18" height="13" rx="2" stroke="currentColor" strokeWidth="1.6" /><path d="M8 21h8M12 17v4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>;
}

function commandLabel(action: EndpointCommandAction, language: 'tr' | 'en') {
  const labels = {
    install: language === 'tr' ? 'Kurulum' : 'Install',
    update: language === 'tr' ? 'Güncelleme' : 'Update',
    uninstall: language === 'tr' ? 'Kaldırma' : 'Uninstall',
    configure: language === 'tr' ? 'Yapılandırma' : 'Configuration',
    restart: language === 'tr' ? 'Yeniden başlatma' : 'Restart',
    set_volume: language === 'tr' ? 'Ses düzeyi' : 'Volume',
    set_content_lock: language === 'tr' ? 'İçerik kilidi' : 'Content lock',
    start_preview: language === 'tr' ? 'Önizleme başlatma' : 'Start preview',
    stop_preview: language === 'tr' ? 'Önizleme kapatma' : 'Stop preview',
  };
  return labels[action];
}

function commandStatus(status: EndpointCommand['status'], language: 'tr' | 'en') {
  const labels = {
    queued: language === 'tr' ? 'Sırada' : 'Queued',
    running: language === 'tr' ? 'İşleniyor' : 'Running',
    succeeded: language === 'tr' ? 'Tamamlandı' : 'Completed',
    failed: language === 'tr' ? 'Başarısız' : 'Failed',
  };
  return labels[status];
}
